import React from 'react';
import { Calendar, Clock, User, BookOpen, CheckCircle, AlertTriangle, Lightbulb, ArrowRight, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const LegalResearchAIPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' 
      ? "Guide Pratique : Maîtriser la Recherche Juridique avec l'IA"
      : "Practical Guide: Mastering Legal Research with AI",
    "datePublished": "2026-01-18T10:00:00+01:00",
    "dateModified": "2026-01-18T10:00:00+01:00",
    "author": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://gaius-tech.com/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png"
      }
    }
  };

  const seoTitle = language === 'fr' 
    ? "Guide Pratique : Recherche Juridique avec l'IA | Gaius"
    : "Practical Guide: Legal Research with AI | Gaius";
    
  const seoDescription = language === 'fr'
    ? "Guide complet pour optimiser votre recherche juridique avec l'intelligence artificielle. Techniques avancées, meilleures pratiques et pièges à éviter."
    : "Complete guide to optimize your legal research with artificial intelligence. Advanced techniques, best practices and pitfalls to avoid.";

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords="recherche juridique IA, intelligence artificielle avocat, recherche jurisprudentielle, base de données juridique, avocat augmenté"
          canonicalUrl="https://gaius-tech.com/articles/recherche-juridique-ia"
          alternateUrlFr="https://gaius-tech.com/articles/recherche-juridique-ia"
          alternateUrlEn="https://gaius-tech.com/articles/legal-research-ai"
          structuredData={structuredData}
          pageType="article"
        />
        
        {/* Article Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="mb-4 bg-emerald-100 text-emerald-800">Do It Yourself</Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Guide pratique : maîtriser la recherche juridique avec l'IA
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Techniques avancées pour exploiter l'intelligence artificielle dans vos recherches jurisprudentielles et documentaires.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>18 janvier 2026</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>15 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=400&fit=crop"
              alt="Recherche juridique avec intelligence artificielle"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-lg leading-relaxed">
              La recherche juridique représente traditionnellement entre 30 et 50 % du temps de travail d'un avocat sur un dossier complexe. L'intelligence artificielle générative, déployée méthodiquement, permet de réduire ce temps de manière significative tout en améliorant la qualité et l'exhaustivité des résultats obtenus.
            </p>

            <p className="text-lg leading-relaxed">
              Ce guide expose les techniques éprouvées par les praticiens les plus avancés des grands cabinets internationaux pour tirer le meilleur parti de ces outils.
            </p>

            <h2 className="text-2xl font-bold mt-12 mb-6">I. Comprendre les capacités et limites des outils</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">1.1 Ce que l'IA générative fait remarquablement bien</h3>

            <p className="text-lg leading-relaxed">
              Les modèles de langage excellent dans plusieurs tâches essentielles à la recherche juridique :
            </p>

            <ul className="space-y-3 my-6">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Synthèse documentaire</strong> : condensation de décisions longues en points essentiels</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Reformulation de requêtes</strong> : génération de termes alternatifs pour élargir ou affiner une recherche</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Identification de concepts connexes</strong> : mise en relation de domaines juridiques distincts</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>Structuration d'analyses</strong> : organisation logique des résultats de recherche</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-8 mb-4">1.2 Les limites critiques à connaître</h3>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-8">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-semibold text-lg mb-2">Mise en garde essentielle</h4>
                  <p className="text-amber-700">
                    Les modèles de langage peuvent produire des références jurisprudentielles inexistantes (« hallucinations »). Toute référence citée par l'IA doit impérativement être vérifiée dans une base de données juridique officielle avant toute utilisation professionnelle.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed">
              Les limitations incluent également :
            </p>

            <ul className="space-y-3 my-6">
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                <span><strong>Date de coupure des données</strong> : les modèles ne connaissent pas les évolutions postérieures à leur entraînement</span>
              </li>
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                <span><strong>Absence de citation directe des sources</strong> : contrairement à Doctrine ou Lexis, l'IA ne renvoie pas systématiquement vers le texte original</span>
              </li>
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                <span><strong>Confidentialité</strong> : les données transmises peuvent être utilisées pour l'entraînement (sauf solutions dédiées)</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold mt-12 mb-6">II. Méthodologie de recherche en cinq étapes</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">Étape 1 : Cadrage du problème juridique</h3>

            <p className="text-lg leading-relaxed">
              Avant toute interaction avec l'IA, formulez clairement :
            </p>

            <ul className="list-disc pl-6 space-y-2 my-6">
              <li>La question juridique précise à laquelle vous cherchez à répondre</li>
              <li>Le domaine de droit concerné (civil, commercial, administratif, pénal)</li>
              <li>La juridiction applicable (française, européenne, internationale)</li>
              <li>La période temporelle pertinente</li>
            </ul>

            <p className="text-lg leading-relaxed">
              Cette étape préliminaire garantit la pertinence des résultats ultérieurs.
            </p>

            <h3 className="text-xl font-semibold mt-8 mb-4">Étape 2 : Formulation de requêtes structurées</h3>

            <p className="text-lg leading-relaxed">
              La qualité des résultats dépend directement de la qualité de la requête. Privilégiez les prompts structurés :
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="mb-4"><strong>Exemple de prompt efficace :</strong></p>
              <p className="italic">
                « Je suis avocat en droit des sociétés. Mon client, une SAS, a un actionnaire minoritaire (15%) qui conteste une augmentation de capital réservée à un tiers. Identifie les principaux fondements juridiques permettant de contester une telle opération en droit français, en distinguant l'abus de majorité, l'abus d'égalité et les manquements au droit à l'information. Cite les arrêts de principe de la Cour de cassation sur chaque fondement. »
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-8 mb-4">Étape 3 : Itération et affinement</h3>

            <p className="text-lg leading-relaxed">
              Procédez par cycles successifs d'affinement :
            </p>

            <ol className="list-decimal pl-6 space-y-2 my-6">
              <li>Requête initiale large pour cartographier le domaine</li>
              <li>Questions de suivi ciblées sur les points identifiés</li>
              <li>Demandes de précision sur les zones d'incertitude</li>
              <li>Vérification croisée avec d'autres sources</li>
            </ol>

            <h3 className="text-xl font-semibold mt-8 mb-4">Étape 4 : Vérification systématique</h3>

            <p className="text-lg leading-relaxed">
              Chaque référence fournie par l'IA doit être vérifiée :
            </p>

            <ul className="list-disc pl-6 space-y-2 my-6">
              <li>Consultez la décision originale sur Légifrance, Doctrine ou Dalloz</li>
              <li>Vérifiez que la solution retenue correspond bien au résumé de l'IA</li>
              <li>Contrôlez que la jurisprudence n'a pas été infirmée ou nuancée ultérieurement</li>
              <li>Confirmez l'applicabilité au cas d'espèce</li>
            </ul>

            <h3 className="text-xl font-semibold mt-8 mb-4">Étape 5 : Documentation du processus</h3>

            <p className="text-lg leading-relaxed">
              Conservez une trace de votre méthodologie de recherche :
            </p>

            <ul className="list-disc pl-6 space-y-2 my-6">
              <li>Prompts utilisés et réponses obtenues</li>
              <li>Sources primaires consultées</li>
              <li>Processus de vérification effectué</li>
              <li>Conclusions retenues</li>
            </ul>

            <p className="text-lg leading-relaxed">
              Cette traçabilité est essentielle en cas de contestation ultérieure et renforce la rigueur professionnelle.
            </p>

            <h2 className="text-2xl font-bold mt-12 mb-6">III. Techniques avancées de prompting juridique</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">3.1 Le prompting par persona</h3>

            <p className="text-lg leading-relaxed">
              Demandez à l'IA d'adopter le point de vue d'un expert spécifique :
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="italic">
                « Agis comme un avocat spécialisé en droit de la concurrence avec 20 ans d'expérience. Analyse les risques d'une clause d'exclusivité de 5 ans dans un contrat de distribution sélective au regard du droit européen. »
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-8 mb-4">3.2 L'analyse contradictoire</h3>

            <p className="text-lg leading-relaxed">
              Explorez systématiquement les deux faces d'une question :
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="italic">
                « D'abord, développe les arguments juridiques en faveur de la validité de cette clause. Ensuite, dans une seconde partie, présente les arguments qui pourraient être invoqués pour contester sa validité. »
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-8 mb-4">3.3 La recherche par analogie</h3>

            <p className="text-lg leading-relaxed">
              Identifiez des solutions dans des domaines connexes :
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="italic">
                « Quels raisonnements développés par la jurisprudence en matière de responsabilité des plateformes numériques pourraient être transposés à la responsabilité des fournisseurs de services d'IA ? »
              </p>
            </div>

            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <Lightbulb className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Conseil stratégique</h4>
                  <p className="text-foreground/80">
                    Combinez systématiquement l'IA générative avec les bases de données juridiques spécialisées (Doctrine, Dalloz, LexisNexis). L'IA identifie les pistes ; les bases de données valident et approfondissent.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mt-12 mb-6">IV. Outils recommandés pour la recherche juridique</h2>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border rounded-lg p-6">
                <h4 className="font-bold mb-3">IA Générative</h4>
                <ul className="space-y-2 text-sm">
                  <li>• ChatGPT-4 / Claude 3.5 pour l'analyse</li>
                  <li>• Harvey pour le contexte juridique</li>
                  <li>• Jimini AI pour le droit français</li>
                  <li>• Lexis AI+ pour la recherche intégrée</li>
                </ul>
              </div>
              <div className="bg-white border rounded-lg p-6">
                <h4 className="font-bold mb-3">Bases de données</h4>
                <ul className="space-y-2 text-sm">
                  <li>• Doctrine pour la jurisprudence</li>
                  <li>• Légifrance pour les textes officiels</li>
                  <li>• Dalloz pour la doctrine</li>
                  <li>• LexisNexis pour les sources internationales</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold mt-12 mb-6">V. Conclusion : vers une recherche augmentée</h2>

            <p className="text-lg leading-relaxed">
              L'intelligence artificielle ne remplace pas l'expertise juridique : elle l'amplifie. Le juriste qui maîtrise ces outils dispose d'un avantage compétitif considérable en termes de rapidité et d'exhaustivité de recherche.
            </p>

            <p className="text-lg leading-relaxed">
              La clé réside dans une utilisation méthodique, critique et complémentaire de l'IA avec les sources traditionnelles. La rigueur déontologique impose de toujours vérifier, croiser et valider les informations obtenues.
            </p>

            <p className="text-lg leading-relaxed">
              Investir dans la maîtrise de ces techniques aujourd'hui, c'est préparer l'avenir de sa pratique professionnelle.
            </p>

          </div>

          {/* CTA Section */}
          <div className="mt-16 pt-8 border-t">
            <h3 className="text-2xl font-bold mb-6">Formations recommandées</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <BookOpen className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation Essentiels IA</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Maîtrisez les fondamentaux de la recherche juridique assistée par IA en 3 modules de 1h30.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Scale className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation Avancée</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Techniques de prompting avancées et intégration dans votre workflow quotidien.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="legal research AI, artificial intelligence lawyer, case law research, legal database, augmented lawyer"
        canonicalUrl="https://gaius-tech.com/articles/legal-research-ai"
        alternateUrlFr="https://gaius-tech.com/articles/recherche-juridique-ia"
        alternateUrlEn="https://gaius-tech.com/articles/legal-research-ai"
        structuredData={structuredData}
        pageType="article"
      />
      
      {/* Article Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4 bg-emerald-100 text-emerald-800">Do It Yourself</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Practical Guide: Mastering Legal Research with AI
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Advanced techniques to leverage artificial intelligence in your case law and documentary research.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>January 18, 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>15 min read</span>
              </span>
              <span className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span>Gaius Team</span>
              </span>
            </div>
          </div>
          <img
            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=400&fit=crop"
            alt="Legal research with artificial intelligence"
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-lg leading-relaxed">
            Legal research traditionally represents between 30 and 50% of a lawyer's working time on complex cases. Generative artificial intelligence, deployed methodically, significantly reduces this time while improving the quality and completeness of results obtained.
          </p>

          <p className="text-lg leading-relaxed">
            This guide outlines proven techniques used by the most advanced practitioners at major international law firms to get the most out of these tools.
          </p>

          <h2 className="text-2xl font-bold mt-12 mb-6">I. Understanding Tool Capabilities and Limitations</h2>

          <h3 className="text-xl font-semibold mt-8 mb-4">1.1 What Generative AI Does Remarkably Well</h3>

          <p className="text-lg leading-relaxed">
            Language models excel at several tasks essential to legal research:
          </p>

          <ul className="space-y-3 my-6">
            <li className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
              <span><strong>Document synthesis</strong>: condensing lengthy decisions into essential points</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
              <span><strong>Query reformulation</strong>: generating alternative terms to broaden or refine a search</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
              <span><strong>Related concept identification</strong>: connecting distinct areas of law</span>
            </li>
            <li className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
              <span><strong>Analysis structuring</strong>: logical organization of research results</span>
            </li>
          </ul>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-8">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-amber-800 font-semibold text-lg mb-2">Essential Warning</h4>
                <p className="text-amber-700">
                  Language models can produce non-existent case law references ("hallucinations"). Any reference cited by the AI must be verified in an official legal database before any professional use.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold mt-12 mb-6">II. Five-Step Research Methodology</h2>

          <p className="text-lg leading-relaxed">
            A systematic approach ensures reliable results:
          </p>

          <ol className="list-decimal pl-6 space-y-4 my-6">
            <li><strong>Problem framing</strong>: Define the precise legal question, applicable jurisdiction, and relevant time period</li>
            <li><strong>Structured query formulation</strong>: Use detailed prompts with context and specific objectives</li>
            <li><strong>Iteration and refinement</strong>: Proceed through successive cycles of narrowing focus</li>
            <li><strong>Systematic verification</strong>: Check every reference against primary sources</li>
            <li><strong>Process documentation</strong>: Maintain records of your methodology for professional accountability</li>
          </ol>

          <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
            <div className="flex items-start">
              <Lightbulb className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-semibold text-lg mb-2">Strategic Advice</h4>
                <p className="text-foreground/80">
                  Systematically combine generative AI with specialized legal databases (Westlaw, LexisNexis, Practical Law). AI identifies leads; databases validate and deepen the research.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* CTA Section */}
        <div className="mt-16 pt-8 border-t">
          <h3 className="text-2xl font-bold mb-6">Recommended Training</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <BookOpen className="w-6 h-6 text-primary" />
                  <h4 className="font-bold">AI Essentials Training</h4>
                </div>
                <p className="text-muted-foreground mb-4">
                  Master the fundamentals of AI-assisted legal research in 3 modules of 1.5 hours.
                </p>
                <Button asChild>
                  <Link to="/training">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Scale className="w-6 h-6 text-primary" />
                  <h4 className="font-bold">Advanced Training</h4>
                </div>
                <p className="text-muted-foreground mb-4">
                  Advanced prompting techniques and integration into your daily workflow.
                </p>
                <Button asChild>
                  <Link to="/training">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
